import { Typography } from '@mui/material'
import { Step } from 'react-joyride'
import { Link } from 'react-router-dom'
import type { Breakpoint } from '~hooks/use-breakpoint'
import { ElectronicDocumentLabel } from '~pages/pages-behind-login/onboarded-vessel/documents-page/components/electronic-document-label'
import { lastStep } from './common-steps'

const smallSizeTourBreakpoints: Breakpoint[] = ['sm', 'xs', 'md']

export const TABLE_SCROLL_TARGET = '.tour-step-documents-table'

export function constructCertificatesTour({
  breakpoint,
  hasCertificateStatusChips,
  hasWriteScope,
  hasVesselSwitch,
  isMtUser,
}: {
  breakpoint: Breakpoint
  hasCertificateStatusChips: boolean
  hasWriteScope?: boolean
  hasVesselSwitch: boolean
  isMtUser?: boolean
}): Step[] {
  return buildCertificatesTour({
    breakpoint,
    hasCertificateStatusChips,
    hasWriteScope,
    hasVesselSwitch,
    isMtUser,
    isTechManager: false,
  })
}

export function constructTechManagerCertificatesTour({
  breakpoint,
  hasCertificateStatusChips,
  hasWriteScope,
}: {
  breakpoint: Breakpoint
  hasCertificateStatusChips: boolean
  hasWriteScope?: boolean
}): Step[] {
  return buildCertificatesTour({
    breakpoint,
    hasCertificateStatusChips,
    hasWriteScope,
    hasVesselSwitch: false,
    isTechManager: true,
  })
}

function buildCertificatesTour({
  breakpoint,
  hasCertificateStatusChips,
  hasWriteScope,
  hasVesselSwitch,
  isMtUser,
  isTechManager,
}: {
  breakpoint: Breakpoint
  hasCertificateStatusChips: boolean
  hasWriteScope?: boolean
  hasVesselSwitch: boolean
  isMtUser?: boolean
  isTechManager: boolean
}): Step[] {
  const isSmallSizeTour = smallSizeTourBreakpoints.includes(breakpoint)

  const editSteps: Step[] = [
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Add a document
          </Typography>
          <Typography variant="paragraph2">
            {isTechManager
              ? 'Click “Upload”, then select or drop one or more files. Each file is scanned to prefill its type, name, categories, issuer, and certificate dates when detected. Review the extracted data, complete any required fields, and then press save.'
              : 'Click “Upload” and select one file. The document is scanned to prefill its type, name, issue date, and expiry date when detected. Review or complete the details, then press save.'}
          </Typography>
        </>
      ),
      target: '.tour-step-documents-upload',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Actions
          </Typography>
          <Typography variant="paragraph2" component="p">
            The Actions column holds the controls for each document:
            {isTechManager ? (
              <ul>
                <li>Download — download the current file when available</li>
                <li>
                  Plus or pencil — open the certificate form. The icon depends
                  on the document source and status. Use the form to update
                  certificate metadata, dates, surveys, tags, remarks, and
                  digital or no-date settings, or upload a new version
                </li>
              </ul>
            ) : (
              <ul>
                <li>Download — download the current file when available</li>
                <li>
                  Plus or pencil — open the document form. The icon depends on
                  the document source and status. Use the form to add your own
                  version or update the issue and expiry dates
                </li>
              </ul>
            )}
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            The document type and document name cannot be modified.
          </Typography>
          {isTechManager && (
            <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
              Existing non-Q88 documents can also be deleted from this form.
            </Typography>
          )}
          {!isTechManager && (
            <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
              If a certificate is not applicable or not available for your
              vessel, check “N/A”.
            </Typography>
          )}
        </>
      ),
      target: '.tour-step-documents-actions-column',
      scrollTarget: TABLE_SCROLL_TARGET,
    },
  ]

  const searchSteps: Step[] = isTechManager
    ? [
        {
          content: (
            <>
              <Typography variant="label" sx={{ mb: 3 }} component="p">
                Search documents
              </Typography>
              <Typography variant="paragraph2">
                Search by document name or certificate category. Use “Filter by
                tag” beside it to narrow the list to documents with selected
                tags.
              </Typography>
            </>
          ),
          target: '.tour-step-documents-filters',
        },
      ]
    : []

  const certificateStatusChipSteps: Step[] = hasCertificateStatusChips
    ? [
        {
          content: (
            <>
              <Typography variant="label" sx={{ mb: 3 }} component="p">
                Certificates requiring attention
              </Typography>
              <Typography variant="paragraph2">
                {isTechManager
                  ? 'These chips highlight certificates that are missing, expired, expiring within 90 days, or have their next survey within 90 days.'
                  : 'These chips highlight certificates that are missing or expired.'}{' '}
                Hover over a chip to see the affected certificates, then select
                one to open its document form.
              </Typography>
            </>
          ),
          target: '.tour-step-documents-status-chips',
        },
      ]
    : []

  const techManagerColumnSteps: Step[] = isTechManager
    ? [
        {
          content: (
            <>
              <Typography variant="label" sx={{ mb: 3 }} component="p">
                Reference number for physical filing system
              </Typography>
              <Typography variant="paragraph2">
                If provided, the document number, referenced to the vessel’s
                physical filing system is shown here
              </Typography>
            </>
          ),
          target: '.tour-step-documents-reference-number',
          scrollTarget: TABLE_SCROLL_TARGET,
        },
        {
          content: (
            <>
              <Typography variant="label" sx={{ mb: 3 }} component="p">
                Document name and indicators
              </Typography>
              <Typography variant="paragraph2" component="p">
                Select a document name to open its preview and download options.
                A link icon shows document tags. Safety Management Certificates
                and ISSC documents also show audit-window information when
                available.
              </Typography>
              <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
                Documents that are only available electronically show this
                label:
              </Typography>
              <ElectronicDocumentLabel sx={{ mt: 2 }} />
            </>
          ),
          target: '.tour-step-documents-name-cell',
          scrollTarget: TABLE_SCROLL_TARGET,
        },
        {
          content: (
            <>
              <Typography variant="label" sx={{ mb: 3 }} component="p">
                Remarks
              </Typography>
              <Typography variant="paragraph2">
                If a document has a comment or remark, it is shown in this
                column.
              </Typography>
            </>
          ),
          target: '.tour-step-documents-remarks-column',
          scrollTarget: TABLE_SCROLL_TARGET,
        },
      ]
    : []

  const techManagerTableSteps: Step[] = isTechManager
    ? [
        {
          content: (
            <>
              <Typography variant="label" sx={{ mb: 3 }} component="p">
                Sort certificates
              </Typography>
              <Typography variant="paragraph2">
                Select the Name or Expiry Date column heading to change the sort
                field and direction.
              </Typography>
            </>
          ),
          target: '.tour-step-documents-sort',
          scrollTarget: TABLE_SCROLL_TARGET,
        },
        {
          content: (
            <>
              <Typography variant="label" sx={{ mb: 3 }} component="p">
                Technical certificate details
              </Typography>
              <Typography variant="paragraph2">
                The table includes issuer, certificate number, issue and expiry
                dates, last and next survey dates, and version history. Version
                History shows when the current record was last updated.
              </Typography>
            </>
          ),
          target: '.tour-step-documents-details-columns',
          scrollTarget: TABLE_SCROLL_TARGET,
        },
      ]
    : []

  const archivedSteps: Step[] = isTechManager
    ? [
        {
          content: (
            <>
              <Typography variant="label" sx={{ mb: 3 }} component="p">
                Archived documents
              </Typography>
              <Typography variant="paragraph2">
                Turn on “Show Archived Documents” to load previous versions.
                Rows with archived versions get a chevron that expands their
                previous certificate data and version timestamp.
              </Typography>
            </>
          ),
          target: '.tour-step-documents-archived',
        },
      ]
    : []

  const commercialViewSteps: Step[] = isTechManager
    ? [
        {
          content: (
            <>
              <Typography variant="label" sx={{ mb: 3 }} component="p">
                Commercial View
              </Typography>
              <Typography variant="paragraph2">
                Use this switch to show the commercial certificate set for the
                vessel. The choice is saved, and switching views clears the
                current search, tag filters, archived view, and document
                selection. This tour covers Technical View, so leave the switch
                off while following it.
              </Typography>
            </>
          ),
          target: '.tour-step-documents-commercial-view',
        },
      ]
    : []

  const vesselSwitchSteps: Step[] =
    isTechManager || !hasVesselSwitch
      ? []
      : [
          {
            content: isMtUser ? (
              <>
                <Typography variant="label" sx={{ mb: 3 }} component="p">
                  Switch to another vessel
                </Typography>
                <Typography variant="paragraph2">
                  Start typing a vessel name or IMO number to jump straight to
                  that vessel's certificates, without going back to the fleet
                  list.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="label" sx={{ mb: 3 }} component="p">
                  My vessels
                </Typography>
                <Typography variant="paragraph2">
                  Pick a vessel from this dropdown to jump straight to that
                  vessel's certificates, without going back to the fleet list.
                </Typography>
              </>
            ),
            target: '.tour-step-vessel-switch',
          },
        ]

  const defaultCertificatesTour: Step[] = [
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Welcome to Certificates
          </Typography>
          <Typography variant="paragraph2" component="p">
            Click through the tour to see what is on this page and how it works.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            You can skip the tour at any point, and you can always start it
            again from the <Link to="/help">Help page</Link>.
          </Typography>
        </>
      ),
      target: 'body',
      placement: 'center',
    },
    ...searchSteps,
    ...(hasWriteScope ? editSteps : []),
    ...certificateStatusChipSteps,

    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Document status
          </Typography>
          <Typography variant="paragraph2">
            The icon in front of each document shows the first matching state,
            in this priority order:
            <ul>
              <li>
                Check icon — the document is marked as not applicable for this
                vessel
              </li>
              <li>Warning icon — the document has passed its expiry date</li>
              <li>
                Information icon — the document is missing. If it is not
                available in Q88, use the Edit icon to upload it
              </li>
              <li>File icon — the document is available</li>
            </ul>
          </Typography>
        </>
      ),
      target: '.tour-step-documents-status-icon',
      scrollTarget: TABLE_SCROLL_TARGET,
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            View data source
          </Typography>
          <Typography variant="paragraph2">
            This column shows where the document came from — for example Q88, or
            the vessel when someone uploaded it.
          </Typography>
        </>
      ),
      target: '.tour-step-documents-source-column',
      scrollTarget: TABLE_SCROLL_TARGET,
    },
    ...techManagerColumnSteps,
    ...techManagerTableSteps,

    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Expand or collapse certificate sections
          </Typography>
          <Typography variant="paragraph2">
            Documents are grouped into categories. Use this chevron to collapse
            or expand a category.
          </Typography>
        </>
      ),
      target: '.tour-step-documents-section-header',
      scrollTarget: TABLE_SCROLL_TARGET,
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Select and share documents
          </Typography>
          <Typography variant="paragraph2">
            Select the documents you need, then download them, copy a link to
            the document package, or send them by email.
          </Typography>
        </>
      ),
      target: '.tour-step-documents-bulk-select',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Export documents
          </Typography>
          <Typography variant="paragraph2">
            Export the list as PDF or Excel, or send it straight to the printer.
          </Typography>
        </>
      ),
      target: '.tour-step-documents-export',
    },
    ...commercialViewSteps,
    ...archivedSteps,
    ...vesselSwitchSteps,

    lastStep,
  ]

  if (!isSmallSizeTour)
    return defaultCertificatesTour.map((step) => ({
      ...step,
      hideCloseButton: true,
      disableCloseOnEsc: true,
    }))

  return defaultCertificatesTour.map((step) => ({
    ...step,
    target: 'body',
    scrollTarget: undefined,
    placement: 'center',
    hideCloseButton: true,
    disableCloseOnEsc: true,
  }))
}
